import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Hospital, HeartPulse, Users, Church, ClipboardCheck, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useReveal } from "../hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTNER_TYPES = [
  { icon: Hospital, label: "Hospitals" },
  { icon: HeartPulse, label: "Rehab Centers" },
  { icon: Church, label: "Faith Communities" },
  { icon: Users, label: "Senior Communities" },
  { icon: ClipboardCheck, label: "Social Workers" },
];

export default function ReferralPartners() {
  const [ref, visible] = useReveal();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    organization: "",
    contact_name: "",
    role: "",
    email: "",
    phone: "",
    partner_type: "",
    notes: "",
  });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.organization || !form.contact_name || !form.email) {
      toast.error("Please fill in organization, your name, and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/partner-inquiry`, form);
      setDone(true);
      toast.success("Partnership inquiry received. We'll be in touch soon.");
    } catch (err) {
      toast.error("Something went wrong. Please call us at 608-513-7639.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="partners"
      data-testid="referral-partners-section"
      className="relative py-24 md:py-32 bg-gradient-to-br from-[#005165] via-[#003946] to-[#005165] text-white overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.07]" style={{
        backgroundImage: 'radial-gradient(circle at 15% 30%, #AC9667 0%, transparent 40%), radial-gradient(circle at 85% 70%, #C4B088 0%, transparent 40%)'
      }} aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
        <div ref={ref} className={`lg:col-span-5 ${visible ? "reveal visible" : "opacity-0"}`}>
          <span className="gold-divider mb-5" style={{ color: "#C4B088" }}>Referral Partners</span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.08]">
            A dependable <em className="not-italic text-[#C4B088]">local resource</em> for the people you care for.
          </h2>
          <p className="mt-6 text-[17px] text-white/80 leading-relaxed max-w-md">
            Madison Peaceful Path Home Care is committed to being a trusted local partner for the
            hospitals, rehab centers, faith communities, senior living organizations, and social
            workers who serve Madison families. Smooth handoffs. Clear communication. Compassionate
            follow-through.
          </p>

          <ul className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {PARTNER_TYPES.map((p) => (
              <li
                key={p.label}
                data-testid={`partner-type-${p.label.replace(/\s/g, "-").toLowerCase()}`}
                className="flex flex-col items-start gap-2 p-3 rounded-xl bg-white/[0.06] border border-white/10 backdrop-blur"
              >
                <p.icon className="w-5 h-5 text-[#C4B088]" strokeWidth={1.5} />
                <span className="text-xs sm:text-sm text-white/90 leading-tight">{p.label}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div data-testid="partner-success" className="bg-white text-[#1F2937] rounded-3xl p-8 md:p-10 text-center">
              <div className="inline-flex w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 items-center justify-center mb-4">
                <ArrowRight className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="font-serif text-3xl text-[#005165] mb-2">Thank you for reaching out.</h3>
              <p className="text-[#4B5563] max-w-md mx-auto">
                Your inquiry has been received. We'll respond personally within one business day to
                discuss how we can partner with your team.
              </p>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="partner-form"
              className="bg-white rounded-3xl p-8 md:p-10 text-[#1F2937] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.4)]"
            >
              <h3 className="font-serif text-3xl text-[#005165] mb-1">Request Partnership</h3>
              <p className="text-sm text-[#4B5563] mb-7">
                Tell us about your team. We'll respond within one business day.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="pt-org">Organization *</Label>
                  <Input id="pt-org" data-testid="pt-org" value={form.organization} onChange={(e) => update("organization", e.target.value)} className="mt-1.5 bg-white border-[#E5DDC9] focus-visible:ring-[#AC9667]" />
                </div>
                <div>
                  <Label htmlFor="pt-name">Your Name *</Label>
                  <Input id="pt-name" data-testid="pt-name" value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} className="mt-1.5 bg-white border-[#E5DDC9]" />
                </div>
                <div>
                  <Label htmlFor="pt-role">Your Role</Label>
                  <Input id="pt-role" data-testid="pt-role" placeholder="e.g. Discharge Planner" value={form.role} onChange={(e) => update("role", e.target.value)} className="mt-1.5 bg-white border-[#E5DDC9]" />
                </div>
                <div>
                  <Label htmlFor="pt-email">Email *</Label>
                  <Input id="pt-email" data-testid="pt-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1.5 bg-white border-[#E5DDC9]" />
                </div>
                <div>
                  <Label htmlFor="pt-phone">Phone</Label>
                  <Input id="pt-phone" data-testid="pt-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-1.5 bg-white border-[#E5DDC9]" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="pt-type">Organization Type</Label>
                  <Select value={form.partner_type} onValueChange={(v) => update("partner_type", v)}>
                    <SelectTrigger id="pt-type" data-testid="pt-type" className="mt-1.5 bg-white border-[#E5DDC9]">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Hospital">Hospital</SelectItem>
                      <SelectItem value="Rehab Center">Rehab Center</SelectItem>
                      <SelectItem value="Faith Community">Faith Community / Church</SelectItem>
                      <SelectItem value="Senior Community">Senior Community</SelectItem>
                      <SelectItem value="Social Worker">Social Worker</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="pt-notes">How can we partner?</Label>
                  <Textarea id="pt-notes" data-testid="pt-notes" rows={4} value={form.notes} onChange={(e) => update("notes", e.target.value)} className="mt-1.5 bg-white border-[#E5DDC9]" />
                </div>
              </div>

              <Button
                data-testid="pt-submit"
                type="submit"
                disabled={loading}
                className="mt-7 bg-[#005165] hover:bg-[#003946] text-white rounded-full h-12 px-8 btn-shimmer"
              >
                {loading ? "Sending..." : "Submit Partnership Request"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
